import { useEffect, useState, useRef } from "react";
import {
  Moon,
  Sun,
  LogOut,
  Settings2,
  EllipsisVertical,
  HelpCircle,
} from "lucide-react";
import NavIconBtn from "./NavIconBtn";
import PopUpNotes from "../../Panels/PopUpNotes";
import PopUpTicket from "../../Panels/PopUpTicket";
import { useTheme } from "../../contexts/ThemeContext";

const DropdownRow = ({ onClick, icon: Icon, label, danger, iconClass }) => (
  <button
    onClick={onClick}
    className={[
      "w-full flex items-center gap-x-3 rounded-lg border border-transparent px-3 py-2 text-sm font-light transition-all duration-200 cursor-pointer",
      danger
        ? "hover:bg-logoutColor/10 hover:border-logoutColor/25"
        : "hover:bg-dropdownItemHover hover:border-velBlueSecond/25",
    ].join(" ")}
  >
    <Icon
      strokeWidth={1.5}
      className={["h-[18px] w-[18px] shrink-0", iconClass]
        .filter(Boolean)
        .join(" ")}
    />
    <span className={danger ? "text-logoutColor" : "text-mutedText"}>
      {label}
    </span>
  </button>
);

const NavMoreMenu = ({
  dark,
  hasTutorial,
  startTutorial,
  openSettings,
  openLogout,
}) => {
  const { darkmode, setDarkmode } = useTheme();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);

  useEffect(() => {
    if (!moreOpen) return;
    const onDoc = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target))
        setMoreOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMoreOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [moreOpen]);

  return (
    <div ref={moreRef} className="relative">
      <NavIconBtn
        onClick={() => setMoreOpen((v) => !v)}
        aria-label="More actions"
        active={moreOpen}
      >
        <EllipsisVertical
          strokeWidth={1.5}
          className={[
            "h-5 w-5 transition-colors duration-200",
            moreOpen ? "text-velBlue" : "text-mutedText/60",
          ].join(" ")}
        />
      </NavIconBtn>

      {moreOpen && (
        <div className="absolute right-0 top-full z-50 mt-2">
          <div className="rounded-lg border border-surfaceBorder bg-dropdownBg/95 p-2 min-w-[200px] backdrop-blur-md shadow-login-panel">
            <PopUpTicket iconOnly />
            <PopUpNotes iconOnly />
            {hasTutorial && (
              <DropdownRow
                onClick={() => {
                  startTutorial();
                  setMoreOpen(false);
                }}
                icon={HelpCircle}
                label="Help & Tutorial"
                iconClass="text-mutedText/60"
              />
            )}
            <DropdownRow
              onClick={() => {
                setDarkmode(!darkmode);
                setMoreOpen(false);
              }}
              icon={dark ? Sun : Moon}
              label={dark ? "Light Mode" : "Dark Mode"}
              iconClass={dark ? "text-sunColor" : "text-mutedText/60"}
            />
            <DropdownRow
              onClick={() => {
                openSettings();
                setMoreOpen(false);
              }}
              icon={Settings2}
              label="Settings"
              iconClass="text-mutedText/60"
            />
            <div
              aria-hidden
              className="my-1 h-px w-full rounded-full bg-dropdownDivider"
            />
            <DropdownRow
              onClick={() => {
                openLogout();
                setMoreOpen(false);
              }}
              icon={LogOut}
              label="Log Out"
              danger
              iconClass="text-logoutColor"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export { DropdownRow };
export default NavMoreMenu;
